import {
  Avatar,
  Badge,
  Box,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FC } from 'react';
import React, { memo, useEffect } from 'react';
import type { BatchReceiptDetails } from './ReceiptBatchForm';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { LabelColor } from '@pflegenavi/web-components';
import { Iconify, Label, OneLineTypography } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import NoReceiptImagesSvg from '../assets/undraw_no_receipt_batch_images.svg';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { scrollIntoViewIfNecessary } from '@pflegenavi/shared/utils';
import { useNavigateReceipts } from '../hooks/useNavigateReceipts';
import type { MangopayReceiptBatchEntry } from '@pflegenavi/frontend/api-nursing-home';
import { MangopayPaymentStatus } from '@pflegenavi/frontend/api-nursing-home';
import { MangopaySingleReceiptAmountLabelTooltip } from '../components/MangopaySingleReceiptAmountLabelTooltip';
import { ReceiptState } from '@pflegenavi/shared/api';

// Receipt states that are shown as their own label on the entry card. Colours
// mirror ReceiptStatusLabel (packages/web-components/src/lib/ReceiptStatusLabel.tsx).
const RECEIPT_STATE_LABEL_COLOR: Partial<Record<ReceiptState, LabelColor>> = {
  [ReceiptState.AwaitingSettlement]: 'info',
  [ReceiptState.Settled]: 'info',
  [ReceiptState.Draft]: 'default',
  [ReceiptState.Incomplete]: 'error',
};

interface ReceiptBatchDetailsReceiptsProps {
  receipts: BatchReceiptDetails[];
  receiptsPayments: MangopayReceiptBatchEntry[] | undefined;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  autoGenerateReceipts: boolean;
}

export const ReceiptBatchDetailsReceipts: FC<
  ReceiptBatchDetailsReceiptsProps
> = ({
  receipts,
  receiptsPayments,
  activeIndex,
  setActiveIndex,
  autoGenerateReceipts,
}) => {
  useNavigateReceipts({
    activeIndex,
    setActiveIndex,
    receiptsLength: receipts.length,
  });
  return (
    <ReceiptBatchDetailsReceiptsView
      receipts={receipts}
      receiptsPayments={receiptsPayments}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      autoGenerateReceipts={autoGenerateReceipts}
    />
  );
};

export interface ReceiptBatchDetailsReceiptsViewProps
  extends ReceiptBatchDetailsReceiptsProps {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  autoGenerateReceipts: boolean;
}

export const ReceiptBatchDetailsReceiptsView: FC<
  ReceiptBatchDetailsReceiptsViewProps
> = ({
  receipts,
  receiptsPayments,
  activeIndex,
  setActiveIndex,
  autoGenerateReceipts,
}) => {
  return (
    <ReceiptsViewContainer sx={{ gap: 2 }}>
      {receipts.map((receipt, index) => (
        <ReceiptBatchDetailsSingleReceipt
          key={receipt.id}
          index={index}
          id={receipt.id as string}
          residentName={receipt.residentName ?? '--'}
          amount={receipt.amount}
          additionalFeeAmount={receipt.additionalFeeAmount}
          individualDate={receipt.individualDate}
          notes={receipt.notes}
          receiptImageIds={receipt.receiptImageIds}
          onClick={() => setActiveIndex(index)}
          active={index === activeIndex}
          residentActive={receipt.residentActive ?? true}
          autoGenerateReceipts={autoGenerateReceipts}
          MangopayReceiptBatchEntry={receiptsPayments?.find(
            (payment) => payment.receipt_batch_entry_id === receipt.id
          )}
        />
      ))}
    </ReceiptsViewContainer>
  );
};

export interface ReceiptBatchDetailsSingleReceiptProps {
  index: number;
  active: boolean;
  id: string;
  residentName: string;
  individualDate?: Date;
  amount?: number;
  additionalFeeAmount?: number;
  notes?: string;
  receiptImageIds?: string[] | null;
  onClick?: () => void;
  residentActive: boolean;
  autoGenerateReceipts: boolean;
  MangopayReceiptBatchEntry?: MangopayReceiptBatchEntry;
}

export const ReceiptBatchDetailsSingleReceipt: FC<
  ReceiptBatchDetailsSingleReceiptProps
> = ({
  index,
  id,
  residentName,
  amount,
  additionalFeeAmount,
  individualDate,
  notes,
  receiptImageIds,
  onClick,
  active,
  residentActive,
  autoGenerateReceipts,
  MangopayReceiptBatchEntry,
}) => {
  const singleReceiptRef = React.useRef<Element | null>(null);

  useEffect(() => {
    if (active && singleReceiptRef.current) {
      scrollIntoViewIfNecessary(singleReceiptRef.current);
    }
  }, [active]);

  return (
    <SingleReceiptContainer onClick={onClick} ref={singleReceiptRef}>
      <SingleReceiptContent
        index={index}
        residentName={residentName}
        amount={amount}
        additionalFeeAmount={additionalFeeAmount}
        individualDate={individualDate}
        receiptImageIds={receiptImageIds}
        notes={notes}
        residentActive={residentActive}
        autoGenerateReceipts={autoGenerateReceipts}
        MangopayReceiptBatchEntry={MangopayReceiptBatchEntry}
      />
      {active && <ActiveIndicator layoutId="active-card-indicator" />}
    </SingleReceiptContainer>
  );
};

interface SingleReceiptContentProps {
  index: number;
  residentName: string;
  residentActive: boolean;
  individualDate?: Date;
  amount?: number;
  additionalFeeAmount?: number;
  notes?: string;
  receiptImageIds?: string[] | null;
  autoGenerateReceipts: boolean;
  MangopayReceiptBatchEntry?: MangopayReceiptBatchEntry;
}

const SingleReceiptContent: FC<SingleReceiptContentProps> = memo(
  ({
    index,
    amount,
    additionalFeeAmount,
    residentName,
    individualDate,
    notes,
    receiptImageIds,
    residentActive,
    autoGenerateReceipts,
    MangopayReceiptBatchEntry,
  }) => {
    const fDate = useFormatDate();
    const { t } = useTranslation();
    const theme = useTheme();

    return (
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.grey['300'],
              }}
            >
              <Typography
                color="primary"
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  mb: '-2px',
                }}
              >
                #{index + 1}
              </Typography>
            </Avatar>
            <Stack
              sx={{
                alignItems: 'flex-start',
                gap: 0.3,
              }}
            >
              <OneLineTypography
                variant="subtitle2"
                // put explicitly width here because the alignItems="flex-start" in the parent stack affects the width of the child
                sx={{
                  width: 200,
                }}
                fontWeight={700}
              >
                {residentName}
              </OneLineTypography>
              <SingleReceiptAmountLabel
                amount={amount ?? 0}
                additionalFeeAmount={additionalFeeAmount ?? 0}
                paymentStatus={MangopayReceiptBatchEntry?.status}
                hasPendingTransfer={
                  MangopayReceiptBatchEntry?.has_pending_transfer
                }
                cancellation={MangopayReceiptBatchEntry?.cancellation}
                receiptState={MangopayReceiptBatchEntry?.receipt_state}
              />
              {individualDate && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                  }}
                >
                  {fDate(new Date(individualDate), YEAR_MONTH_DAY_SHORT_FORMAT)}
                </Typography>
              )}
            </Stack>
          </Stack>

          {receiptImageIds && receiptImageIds.length > 0 ? (
            <div style={{ position: 'relative' }}>
              <Badge
                badgeContent={receiptImageIds.length}
                invisible={receiptImageIds.length < 2}
                color="info"
                sx={{
                  '& .MuiBadge-badge': { fontSize: 12 },
                  left: 30,
                  top: -28,
                }}
              />
              <Iconify
                icon="mdi:receipt-text"
                color={theme.palette.success.main}
                fontSize={35}
              />
            </div>
          ) : autoGenerateReceipts ? (
            <Tooltip
              title={t('receipts.batch.one-receipt-will-be-auto-generated')}
            >
              <div>
                <Iconify
                  icon="mdi:receipt-text"
                  color={theme.palette.info.main}
                  fontSize={35}
                />
              </div>
            </Tooltip>
          ) : (
            <Tooltip title={t('receipts.batch.form.receipt-image-unavailable')}>
              <div>
                <Iconify
                  icon="mdi:receipt-text"
                  color={theme.palette.grey['400']}
                  fontSize={35}
                />
              </div>
            </Tooltip>
          )}
        </Stack>
        {notes && <Notes notes={notes} />}
      </Stack>
    );
  }
);

interface SingleReceiptAmountLabelProps {
  amount: number;
  additionalFeeAmount: number;
  paymentStatus: MangopayPaymentStatus | undefined;
  hasPendingTransfer?: boolean;
  cancellation?: { date: Date; note: string };
  receiptState?: ReceiptState | null;
}

const SingleReceiptAmountLabel: FC<SingleReceiptAmountLabelProps> = ({
  amount,
  additionalFeeAmount,
  paymentStatus,
  hasPendingTransfer,
  cancellation,
  receiptState,
}) => {
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();

  if (paymentStatus === MangopayPaymentStatus.ReceiptDeleted) {
    return (
      <Label color="error" variant="ghost">
        {fCurrencyCents(amount)} ({t('receipts.batch.receipt-deleted')})
      </Label>
    );
  }

  if (cancellation) {
    return (
      <Tooltip
        title={
          cancellation.note || t('receipts.batch.details.payment.cancelled')
        }
      >
        <span>
          <Label color="error" variant="ghost">
            {fCurrencyCents(amount)} (
            {t('receipts.batch.details.payment.cancelled')})
          </Label>
        </span>
      </Tooltip>
    );
  }

  const stateLabelColor = receiptState
    ? RECEIPT_STATE_LABEL_COLOR[receiptState]
    : undefined;

  if (stateLabelColor) {
    return (
      <Label color={stateLabelColor} variant="ghost">
        {fCurrencyCents(amount)} ({t(`receipts.status.${receiptState}`)})
      </Label>
    );
  }

  if (paymentStatus === MangopayPaymentStatus.Pending) {
    return (
      <MangopaySingleReceiptAmountLabelTooltip
        hasPendingTransfer={hasPendingTransfer ?? false}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <Label color="warning" variant="ghost">
            {fCurrencyCents(amount)}
          </Label>
          {additionalFeeAmount > 0 && (
            <Label color="warning">
              +&nbsp;{fCurrencyCents(additionalFeeAmount)}
            </Label>
          )}
        </Stack>
      </MangopaySingleReceiptAmountLabelTooltip>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1,
      }}
    >
      <Label color="success" variant="ghost">
        {fCurrencyCents(amount)}
      </Label>
      {additionalFeeAmount > 0 && (
        <Label color="default">
          +&nbsp;{fCurrencyCents(additionalFeeAmount)}
        </Label>
      )}
    </Stack>
  );
};

interface NotesProps {
  notes: string | null;
}

const Notes: FC<NotesProps> = ({ notes }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={notes} arrow placement="bottom">
      <Stack
        sx={{
          maxWidth: '100%',
          px: 2,
          py: 1,
          backgroundColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {t('receipts.batch.note')}
        </Typography>
        <OneLineTypography variant="caption" fontSize={13.5} color="grey.800">
          {notes}
        </OneLineTypography>
      </Stack>
    </Tooltip>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NoReceiptsAdded = () => {
  const { t } = useTranslation();
  return (
    <Stack
      sx={{
        alignItems: 'center',
        p: 3,
        flex: 1,
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          gap: 2,
          flex: 1,
        }}
      >
        <img src={NoReceiptImagesSvg} width={200} />
        <Stack
          sx={{
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              textTransform: 'uppercase',
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            {t('receipts.batch.no-receipt-available')}
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
            }}
          >
            {t('receipts.batch.no-receipt-available-description')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ReceiptsViewContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    overflowY: 'auto',
    maxHeight: '70vh',
    width: 350,
    overflowX: 'hidden',
    direction: 'rtl',
    pl: 1,
    ml: -1,
    pb: 2,
    '&::-webkit-scrollbar': {
      width: '0.3em',
      color: 'red',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0,0,0,.1)',
    },
    position: 'relative',
    zIndex: 0,
  })
);

const SingleReceiptContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withNotes',
})(
  ({ theme }) =>
    ({ theme }) =>
      theme.unstable_sx({
        position: 'relative',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        boxShadow: theme.customShadows.card,
        borderRadius: 2,
        p: 2.5,
        width: 340,
        maxHeight: 250,
        cursor: 'pointer',
        userSelect: 'none',
        direction: 'ltr',
        overflow: 'visible',
        zIndex: undefined,
      })
);

const ActiveIndicator = styled(motion.div)(({ theme }) =>
  theme.unstable_sx({
    backgroundColor: 'success.main',
    width: 5,
    right: 0,
    top: 0,
    bottom: 0,
    position: 'absolute',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  })
);
